import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus, BrowserAuthError } from '@azure/msal-browser';
import { loginRequest } from '../auth/msalConfig';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [token, setToken] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [isGuest, setIsGuest] = useState(false);

  const userId = accounts[0]?.localAccountId ?? null;

  const acquireToken = useCallback(async () => {
    if (!accounts[0]) return;
    try {
      const result = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      setToken(result.accessToken);
      setAuthError(null);
    } catch (err) {
      // Only a redirect can resolve an interaction-required error. iOS Safari's
      // tracking prevention blocks the hidden-iframe silent renewal, so this is
      // the common path there; the redirect re-establishes a first-party session.
      // A timed-out hidden-iframe renewal is just as unrecoverable silently
      // (e.g. the iframe was too slow to relay the response), so it takes the
      // same redirect path rather than stranding the user on an error screen.
      const silentRenewalTimedOut =
        err instanceof BrowserAuthError && err.errorCode === 'timed_out';
      if (err instanceof InteractionRequiredAuthError || silentRenewalTimedOut) {
        try {
          await instance.acquireTokenRedirect({ ...loginRequest, account: accounts[0] });
        } catch (redirectErr) {
          setAuthError(redirectErr);
        }
      } else {
        // Any other failure must surface rather than leave the app on a blank
        // screen waiting for a token that will never arrive.
        console.error('[auth] token acquisition failed', err);
        setAuthError(err);
      }
    }
  }, [instance, accounts]);

  useEffect(() => {
    // Wait until MSAL has finished any in-flight interaction (e.g. processing the
    // login redirect) before attempting silent token acquisition.
    if (inProgress !== InteractionStatus.None) return;
    if (isAuthenticated) {
      setIsGuest(false); // Signing in supersedes guest mode
      acquireToken();
    } else {
      setToken(null);
    }
  }, [isAuthenticated, acquireToken, inProgress]);

  function login() {
    return instance.loginRedirect(loginRequest);
  }

  function logout() {
    return instance.logoutRedirect();
  }

  function enterGuest() {
    setIsGuest(true);
  }

  function exitGuest() {
    setIsGuest(false);
  }

  return (
    <AuthContext.Provider value={{ token, authError, userId, login, logout, isLoggedIn: isAuthenticated, isGuest, enterGuest, exitGuest }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
